import { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import { Leaf, Package, ShoppingBag, LogOut, Plus, Pencil, Trash2, X, Eye, EyeOff, Star } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
  adminGetOrders
} from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) navigate("/admin/login");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin/login");
  };

  const sideLinks = [
    { label: "Productos", path: "/admin", icon: Package },
    { label: "Pedidos", path: "/admin/orders", icon: ShoppingBag },
  ];

  return (
    <div data-testid="admin-dashboard" className="grid grid-cols-1 md:grid-cols-[260px_1fr] min-h-screen bg-[#F9FAFB]">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="bg-[#1A1A1A] text-[#E5E5E5] p-6 hidden md:flex flex-col">
        <div className="flex items-center gap-2 mb-10">
          <Leaf className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
          <span className="font-heading text-lg font-bold">Oymer Admin</span>
        </div>

        <nav className="space-y-1 flex-1">
          {sideLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`admin-nav-${link.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-body text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-forest text-white"
                    : "text-[#999] hover:text-white hover:bg-white/5"
                }`}
              >
                <link.icon size={18} strokeWidth={1.5} />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
          <Link to="/" className="block font-body text-xs text-[#666] hover:text-white transition-colors duration-200 px-4 py-1">
            Ver tienda
          </Link>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 px-4 py-2 font-body text-sm text-[#999] hover:text-white transition-colors duration-200"
          >
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile nav */}
      <div className="md:hidden bg-[#1A1A1A] p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Leaf className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
          <span className="font-heading text-base font-bold">Oymer Admin</span>
        </div>
        <div className="flex gap-2">
          {sideLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`p-2 rounded-lg ${location.pathname === link.path ? "bg-forest text-white" : "text-[#999]"}`}
            >
              <link.icon size={18} />
            </Link>
          ))}
          <button onClick={handleLogout} className="p-2 text-[#999]"><LogOut size={18} /></button>
        </div>
      </div>

      {/* Content */}
      <main className="p-6 md:p-10 overflow-auto">
        <Routes>
          <Route index element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
        </Routes>
      </main>
    </div>
  );
}

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", short_description: "", price_text: "Consultar precio",
    category: "Tés", image_url: "", benefits: [], ingredients: "", featured: false, active: true,
  });
  const [benefitInput, setBenefitInput] = useState("");

  const loadProducts = async () => {
    try {
      const res = await adminGetProducts();
      setProducts(res.data);
    } catch (e) {
      toast.error("Error cargando productos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProducts(); }, []);

  const resetForm = () => {
    setForm({
      name: "", description: "", short_description: "", price_text: "Consultar precio",
      category: "Tés", image_url: "", benefits: [], ingredients: "", featured: false, active: true,
    });
    setBenefitInput("");
    setEditing(null);
  };

  const openNew = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (product) => {
    setEditing(product.id);
    setForm({
      name: product.name, description: product.description, short_description: product.short_description || "",
      price_text: product.price_text || "Consultar precio", category: product.category,
      image_url: product.image_url || "", benefits: product.benefits || [], ingredients: product.ingredients || "",
      featured: product.featured || false, active: product.active !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminUpdateProduct(editing, form);
        toast.success("Producto actualizado");
      } else {
        await adminCreateProduct(form);
        toast.success("Producto creado");
      }
      setDialogOpen(false);
      resetForm();
      loadProducts();
    } catch (e) {
      toast.error("Error guardando producto");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await adminDeleteProduct(id);
      toast.success("Producto eliminado");
      loadProducts();
    } catch (e) {
      toast.error("Error eliminando producto");
    }
  };

  const addBenefit = () => {
    if (benefitInput.trim()) {
      setForm({ ...form, benefits: [...form.benefits, benefitInput.trim()] });
      setBenefitInput("");
    }
  };

  const removeBenefit = (i) => {
    setForm({ ...form, benefits: form.benefits.filter((_, idx) => idx !== i) });
  };

  const categories = ["Tés", "Cafés", "Suplementos", "Cuidado Personal"];

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-gray-900">Productos</h1>
          <p className="font-body text-sm text-gray-500 mt-1">{products.length} productos en catálogo</p>
        </div>
        <button
          onClick={openNew}
          data-testid="admin-add-product-button"
          className="bg-forest text-white rounded-lg px-4 py-2.5 font-body text-sm font-medium transition-colors hover:bg-forest-dark flex items-center gap-2"
        >
          <Plus size={16} /> Nuevo Producto
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 animate-pulse">
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-gray-100 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-1/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((product) => (
            <div
              key={product.id}
              data-testid={`admin-product-row-${product.id}`}
              className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow duration-200"
            >
              <img
                src={product.image_url}
                alt={product.name}
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-body text-sm font-semibold text-gray-900 truncate">{product.name}</h3>
                  {product.featured && <Star size={14} className="text-amber-500 fill-amber-500 flex-shrink-0" />}
                </div>
                <p className="font-body text-xs text-gray-500 mt-0.5">{product.category}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className={`inline-block w-2 h-2 rounded-full ${product.active ? "bg-green-500" : "bg-gray-300"}`} />
                <span className="font-body text-xs text-gray-500">{product.active ? "Activo" : "Inactivo"}</span>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => openEdit(product)}
                  data-testid={`admin-edit-product-${product.id}`}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-forest transition-colors duration-200"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  data-testid={`admin-delete-product-${product.id}`}
                  className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors duration-200"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? "Editar Producto" : "Nuevo Producto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-xs font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  data-testid="admin-product-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                  required
                />
              </div>
              <div>
                <label className="block font-body text-xs font-medium text-gray-700 mb-1">Categoría *</label>
                <select
                  data-testid="admin-product-category-select"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 bg-white"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block font-body text-xs font-medium text-gray-700 mb-1">Descripción corta</label>
              <input
                type="text"
                data-testid="admin-product-short-desc-input"
                value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
              />
            </div>

            <div>
              <label className="block font-body text-xs font-medium text-gray-700 mb-1">Descripción *</label>
              <textarea
                data-testid="admin-product-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 resize-none"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-xs font-medium text-gray-700 mb-1">Texto de precio</label>
                <input
                  type="text"
                  data-testid="admin-product-price-text-input"
                  value={form.price_text}
                  onChange={(e) => setForm({ ...form, price_text: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                />
              </div>
              <div>
                <label className="block font-body text-xs font-medium text-gray-700 mb-1">URL de imagen</label>
                <input
                  type="url"
                  data-testid="admin-product-image-input"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                />
              </div>
            </div>

            <div>
              <label className="block font-body text-xs font-medium text-gray-700 mb-1">Ingredientes</label>
              <input
                type="text"
                data-testid="admin-product-ingredients-input"
                value={form.ingredients}
                onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
              />
            </div>

            <div>
              <label className="block font-body text-xs font-medium text-gray-700 mb-1">Beneficios</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  data-testid="admin-product-benefit-input"
                  value={benefitInput}
                  onChange={(e) => setBenefitInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBenefit())}
                  placeholder="Agregar beneficio"
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
                />
                <button
                  type="button"
                  onClick={addBenefit}
                  className="bg-forest/10 text-forest rounded-lg px-3 py-2 text-sm font-body font-medium hover:bg-forest/20 transition-colors"
                >
                  Agregar
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.benefits.map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-forest/5 text-forest rounded-full px-3 py-1 text-xs font-body">
                    {b}
                    <button type="button" onClick={() => removeBenefit(i)} className="hover:text-red-500">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="admin-product-featured-checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="rounded border-gray-300 text-forest focus:ring-forest"
                />
                <span className="font-body text-sm text-gray-700 flex items-center gap-1">
                  <Star size={14} /> Destacado
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="admin-product-active-checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="rounded border-gray-300 text-forest focus:ring-forest"
                />
                <span className="font-body text-sm text-gray-700 flex items-center gap-1">
                  {form.active ? <Eye size={14} /> : <EyeOff size={14} />} Activo
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => { setDialogOpen(false); resetForm(); }}
                className="px-4 py-2 rounded-lg font-body text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="admin-product-submit-button"
                className="bg-forest text-white rounded-lg px-6 py-2 font-body text-sm font-medium transition-colors hover:bg-forest-dark"
              >
                {editing ? "Guardar Cambios" : "Crear Producto"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminGetOrders()
      .then((res) => setOrders(res.data))
      .catch(() => toast.error("Error cargando pedidos"))
      .finally(() => setLoading(false));
  }, []);

  const statusColors = {
    pending: "bg-amber-100 text-amber-700",
    contacted: "bg-blue-100 text-blue-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
  };

  const statusLabels = {
    pending: "Pendiente",
    contacted: "Contactado",
    completed: "Completado",
    cancelled: "Cancelado",
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-semibold text-gray-900">Pedidos</h1>
        <p className="font-body text-sm text-gray-500 mt-1">{orders.length} pedidos recibidos</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-1/3 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
          <ShoppingBag size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="font-body text-gray-500">No hay pedidos aún</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <div
              key={order.id}
              data-testid={`admin-order-row-${order.id}`}
              className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-body text-sm font-semibold text-gray-900">{order.customer_name}</h3>
                  <p className="font-body text-xs text-gray-500 mt-0.5">
                    {order.customer_phone} {order.customer_email && `• ${order.customer_email}`}
                  </p>
                  {order.products.length > 0 && (
                    <p className="font-body text-xs text-gray-400 mt-1">
                      Productos: {order.products.join(", ")}
                    </p>
                  )}
                  {order.message && (
                    <p className="font-body text-xs text-gray-400 mt-1 italic">"{order.message}"</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${statusColors[order.status] || statusColors.pending}`}>
                    {statusLabels[order.status] || order.status}
                  </span>
                  <span className="font-body text-xs text-gray-400">
                    {new Date(order.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
